import { SectionShell } from "@/components/design-system/SectionShell";
import {
  TestimonialCarousel,
  type TestimonialItem,
} from "@/components/ui/testimonial-carousel";

// Sample data — reference only. These render on the admin-gated design system
// page, never the public site. No profile images, so the cards show the initials
// monogram fallback.
const SAMPLE: TestimonialItem[] = [
  {
    quote:
      "They found budget we'd been quietly wasting for months and turned it into leads inside the first fortnight.",
    name: "Sarah Whitfield",
    attribution: "Founder, Whitfield & Co",
  },
  {
    quote:
      "Straight answers, no agency jargon. Our cost per lead dropped and I finally understand the account.",
    name: "Daniel Osei",
    attribution: "Marketing Lead, Northgate Homes",
  },
  {
    quote:
      "A specialist owning the account instead of an agency junior made all the difference to our numbers.",
    name: "Priya Nair",
    attribution: "Director, Lumen Clinics",
  },
  {
    quote:
      "Clear reporting I can actually act on, and testing that never stops. The pipeline has never been healthier.",
    name: "Tom Beckett",
    attribution: "Owner, Beckett Joinery",
  },
];

const code = `import { TestimonialCarousel } from "@/components/ui/testimonial-carousel"

<TestimonialCarousel
  items={[
    {
      quote: "They found budget we'd been quietly wasting for months...",
      name: "Sarah Whitfield",
      attribution: "Founder, Whitfield & Co",
      profileImage: "/uploads/sarah.jpg", // optional; monogram fallback if omitted
    },
    // ...more
  ]}
/>`;

export function TestimonialSection() {
  return (
    <SectionShell
      id="testimonial"
      title="Testimonial carousel"
      description={
        <>
          Client quotes as a horizontal carousel that auto-scrolls, pauses on
          hover/focus, and reveals prev/next arrows while you interact. Each card
          renders semantic <code>&lt;figure&gt;</code> /{" "}
          <code>&lt;blockquote&gt;</code> / <code>&lt;figcaption&gt;</code> markup,
          a circular photo (or a blank placeholder when none is given), and is
          styled with design tokens — so it reads correctly on the light-locked
          marketing page and in light or dark here. No animation library; scrolling
          is native, SSR-safe, and skipped when the user prefers reduced motion.
        </>
      }
      whenToUse={
        <ul>
          <li>Client or customer quotes as social proof, several in a row.</li>
          <li>Any set of short attributed quotations with optional headshots.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            A headline metric or KPI — use <code>Stat</code> or{" "}
            <code>ResultCard</code>.
          </li>
          <li>
            A single pull quote inside long-form copy — use a bare{" "}
            <code>&lt;blockquote&gt;</code>.
          </li>
          <li>
            Placeholder or invented quotes on the public site — testimonials
            launch hidden until real quotes exist.
          </li>
        </ul>
      }
      preview={<TestimonialCarousel items={SAMPLE} />}
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            Each item takes <code>quote</code>, <code>name</code>, optional{" "}
            <code>attribution</code>, and optional <code>profileImage</code>.
            Without a photo the card shows a blank image placeholder.
          </li>
          <li>
            Auto-advances every 4s (override with <code>autoAdvanceMs</code>;{" "}
            <code>0</code> disables). Pauses on hover/focus, and the track also
            scrolls by touch/trackpad. The single <code>Testimonial</code> card and
            the generic <code>Carousel</code> are exported for standalone use.
          </li>
        </ul>
      }
    />
  );
}
